import React from 'react'
import { motion } from 'framer-motion'
import clsx from 'clsx'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
  fullWidth?: boolean
  animated?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      loading = false,
      icon,
      iconPosition = 'left',
      fullWidth = false,
      animated = true,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden'
    
    const variants = {
      primary: 'bg-gradient-snwolley text-white hover:shadow-snwolley focus:ring-snwolley-green-500 disabled:hover:shadow-none',
      secondary: 'bg-white text-gray-900 border border-gray-300 hover:bg-gray-50 focus:ring-gray-500',
      danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
      ghost: 'text-snwolley-green-600 hover:bg-snwolley-green-50 focus:ring-snwolley-green-500',
    }
    
    const sizes = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-sm',
      lg: 'px-6 py-3 text-base',
    }
    
    const iconSizes = {
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-6 h-6',
    }

    if (animated) {
      const { 
        onAnimationStart, 
        onAnimationEnd, 
        onAnimationIteration,
        onDragStart,
        onDragEnd,
        onDrag,
        ...buttonProps 
      } = props
      return (
        <motion.button
          className={clsx(
            baseClasses,
            variants[variant],
            sizes[size],
            fullWidth && 'w-full',
            className
          )}
          disabled={disabled || loading}
          ref={ref}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          {...buttonProps}
        >
        {loading ? (
          <div className="flex items-center">
            <svg
              className={clsx('animate-spin -ml-1 mr-2', iconSizes[size])}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Loading...
          </div>
        ) : (
          <>
            {icon && iconPosition === 'left' && (
              <span className={clsx('mr-2', iconSizes[size])}>{icon}</span>
            )}
            {children}
            {icon && iconPosition === 'right' && (
              <span className={clsx('ml-2', iconSizes[size])}>{icon}</span>
            )}
          </>
        )}
        
          {/* Ripple effect for primary buttons */}
          {variant === 'primary' && (
            <motion.div
              className="absolute inset-0 bg-white opacity-0 pointer-events-none"
              initial={false}
              whileHover={{ opacity: 0.1 }}
              transition={{ duration: 0.2 }}
            />
          )}
        </motion.button>
      )
    }
    
    return (
      <button
        className={clsx(
          baseClasses,
          variants[variant],
          sizes[size],
          fullWidth && 'w-full',
          className
        )}
        disabled={disabled || loading}
        ref={ref}
        {...props}
      >
        {loading ? (
          <div className="flex items-center">
            <svg
              className={clsx('animate-spin -ml-1 mr-2', iconSizes[size])}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Loading...
          </div>
        ) : (
          <>
            {icon && iconPosition === 'left' && (
              <span className={clsx('mr-2', iconSizes[size])}>{icon}</span>
            )}
            {children}
            {icon && iconPosition === 'right' && (
              <span className={clsx('ml-2', iconSizes[size])}>{icon}</span>
            )}
          </>
        )}
      </button>
    )
  }
)

Button.displayName = 'Button'

export default Button